/**
 * DecisionFlow — ClawbackPolicyManager
 * data-fit LLC · Confidential
 * Domain: P3-04 Churn Clawback (Standard tier — feature_gate_key = NULL)
 * Spec authority: decisionflow_phase3_plan_spec_v1_0.md §5
 * API Contract: api_contract_v1_32.md §50
 * Wizard substages:
 *   churn_clawback_policy_definition · churn_clawback_policy_approval · churn_event_load
 *
 * Three tabs:
 *   Tab 1 — Policies: create / edit / submit / approve clawback_policy per plan_component
 *   Tab 2 — Churn Events: ingest single events, view event list with filters
 *   Tab 3 — Results: view churn_clawback_result rows for a calculation run
 *
 * DDL ground truth (phase3_plan_spec §5.2):
 *   clawback_policy:
 *     clawback_policy_id bigint PK, tenant_id uuid, plan_component_id int FK,
 *     policy_name varchar(200), protection_window_days int,
 *     clawback_pct numeric(5,4), applies_to_revenue_types varchar(200) nullable,
 *     is_active bool, effective_from date, effective_to date,
 *     submitted_by uuid, submitted_at timestamptz,
 *     approved_by uuid, approved_at timestamptz,
 *     created_by uuid, created_at timestamptz, updated_at timestamptz
 *
 *   churn_event:
 *     churn_event_id uuid PK, tenant_id uuid,
 *     customer_identifier varchar(200), churned_mrr numeric(18,4),
 *     churn_currency_code char(3), churn_date date,
 *     churn_reason_code varchar(60) nullable, source_system varchar(100),
 *     source_event_id varchar(200), import_log_id uuid nullable,
 *     created_by uuid, created_at timestamptz
 *     UNIQUE(tenant_id, source_system, source_event_id)
 *
 *   churn_clawback_result:
 *     clawback_result_id uuid PK, calculation_run_id uuid, position_id uuid,
 *     fiscal_period_id int, churn_event_id uuid, clawback_policy_id bigint,
 *     source_payout_detail_id uuid, source_payout_summary_id uuid,
 *     original_commission numeric(18,4), clawback_amount numeric(18,4),
 *     clawback_currency char(3), days_since_close int, within_window bool,
 *     created_at timestamptz
 *
 * API surface (api_contract_v1_32.md §50):
 *   POST   /api/v1/churn-events                                     — ingest single event
 *   POST   /api/v1/churn-events/import                              — CSV batch (multipart)
 *   GET    /api/v1/churn-events?churn_date_from=&churn_date_to=&customer_identifier=
 *   GET    /api/v1/churn-events/{churn_event_id}
 *   POST   /api/v1/clawback-policies
 *   GET    /api/v1/clawback-policies?plan_component_id={id}
 *   GET    /api/v1/clawback-policies/{clawback_policy_id}
 *   PUT    /api/v1/clawback-policies/{clawback_policy_id}           — raw fetch
 *   POST   /api/v1/clawback-policies/{clawback_policy_id}/submit
 *   POST   /api/v1/clawback-policies/{clawback_policy_id}/approve
 *   GET    /api/v1/churn-clawback-results?calculation_run_id={id}&position_id={id}
 *
 * Architecture rules:
 *   - useAuth() for { user } — no token in props (ARCH-2)
 *   - apiGet / apiPost from client — PUT / CSV import via raw fetch
 *   - tenant_id never in any request body
 *   - No window.prompt / window.confirm — inline confirmation state
 *   - ApprovalPanel for clawback_policy (has submitted_by/submitted_at DDL columns)
 *   - onReject={null} — reject not in §50
 *   - CSV import uses raw fetch (multipart/form-data — not supported by apiPost)
 *
 * Props:
 *   mode             — 'wizard' | 'admin'
 *   period           — { fiscal_period_id, period_code } | null
 *   wizardSteps      — array
 *   embedded         — bool  — when true: context injection mode (spec §4.8)
 *   planId           — int   — injected plan id (embedded only)
 *   planComponentId  — int   — injected component id, skip selector
 *   fiscalPeriodId   — int   — injected fiscal period (embedded only)
 *   readOnly         — bool  — true when plan is approved/archived (wizard context)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Constants — DDL-authoritative
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'policies', label: 'Clawback Policies' },
  { key: 'events',   label: 'Churn Events'      },
  { key: 'results',  label: 'Run Results'        },
];

const CURRENCY_CODES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'SGD', 'JPY', 'CNY'];

// Revenue type codes for applies_to_revenue_types filter (allow_on_import = TRUE types)
const REVENUE_TYPE_OPTIONS = [
  { value: 'new_logo',       label: 'New Logo' },
  { value: 'expansion',      label: 'Expansion' },
  { value: 'renewal',        label: 'Renewal' },
  { value: 'upsell',         label: 'Upsell' },
  { value: 'churn_recovery', label: 'Churn Recovery' },
];

const EMPTY_POLICY_FORM = {
  policy_name:               '',
  protection_window_days:    '90',
  clawback_pct:              '1.0000',
  applies_to_revenue_types:  [], // multi-select → joined as comma-separated on POST
  is_active:                 true,
  effective_from:            '',
  effective_to:              '',
};

const EMPTY_EVENT_FORM = {
  customer_identifier: '',
  churned_mrr:         '',
  churn_currency_code: 'USD',
  churn_date:          '',
  churn_reason_code:   '',
  source_system:       '',
  source_event_id:     '',
};

// ---------------------------------------------------------------------------
// Shared CSS class strings — token-compliant
// ---------------------------------------------------------------------------

const thClass =
  'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide ' +
  'text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] ' +
  'border-b border-[rgb(var(--color-border))]';

const tdClass =
  'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] ' +
  'border-b border-[rgb(var(--color-border))]';

const btnPrimary =
  'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium ' +
  'text-white hover:bg-[rgb(var(--color-primary-hover))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const btnSecondary =
  'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] ' +
  'px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] ' +
  'hover:bg-[rgb(var(--color-surface-muted))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const inputClass =
  'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] ' +
  'bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] ' +
  'px-3 py-2 text-tok-sm focus:outline-none ' +
  'focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';

const labelClass = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (!entity) return 'draft';
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getErrorMessage(error, fallback) {
  return error?.message || error?.detail || fallback;
}

function getApiErrorCode(error) {
  return error?.code || null;
}

function fmtDate(val) {
  if (!val) return '—';
  return String(val).slice(0, 10);
}

function fmtMoney(val) {
  if (val === null || val === undefined || val === '') return '—';
  return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(val) {
  if (val === null || val === undefined || val === '') return '—';
  return `${(Number(val) * 100).toFixed(2)}%`;
}

// Parse comma-separated applies_to_revenue_types into array
function parseRevTypes(str) {
  if (!str) return [];
  return str.split(',').map((s) => s.trim()).filter(Boolean);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ClawbackPolicyManager({ mode, period, wizardSteps, embedded = false, planId, planComponentId, fiscalPeriodId, readOnly = false }) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.clawback')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  const [activeTab, setActiveTab]                   = useState('policies');
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [toast, setToast]                           = useState(null);

  // Plan component selector
  const [components, setComponents]                 = useState([]);
  const [componentsLoading, setComponentsLoading]   = useState(false);
  const [componentsError, setComponentsError]       = useState('');
  const [selectedComponentId, setSelectedComponentId] = useState('');

  // Policies
  const [policies, setPolicies]                     = useState([]);
  const [policiesLoading, setPoliciesLoading]       = useState(false);
  const [policiesError, setPoliciesError]           = useState('');
  const [selectedPolicy, setSelectedPolicy]         = useState(null);
  const [detailLoading, setDetailLoading]           = useState(false);
  const [actionLoading, setActionLoading]           = useState(false);
  const [actionError, setActionError]               = useState('');

  // Policy form
  const [showPolicyForm, setShowPolicyForm]         = useState(false);
  const [policyForm, setPolicyForm]                 = useState(EMPTY_POLICY_FORM);
  const [policySaving, setPolicySaving]             = useState(false);
  const [policyFormError, setPolicyFormError]       = useState('');
  const [editingPolicyId, setEditingPolicyId]       = useState(null);

  // Churn events
  const [events, setEvents]                         = useState([]);
  const [eventsLoading, setEventsLoading]           = useState(false);
  const [eventsError, setEventsError]               = useState('');
  const [eventFilters, setEventFilters]             = useState({ from: '', to: '', customer: '' });
  const [showEventForm, setShowEventForm]           = useState(false);
  const [eventForm, setEventForm]                   = useState(EMPTY_EVENT_FORM);
  const [eventSaving, setEventSaving]               = useState(false);
  const [eventFormError, setEventFormError]         = useState('');
  // CSV import
  const [csvFile, setCsvFile]                       = useState(null);
  const [csvImporting, setCsvImporting]             = useState(false);
  const [csvImportResult, setCsvImportResult]       = useState(null);
  const [csvImportError, setCsvImportError]         = useState('');

  // Results tab
  const [runIdInput, setRunIdInput]                 = useState('');
  const [positionIdInput, setPositionIdInput]       = useState('');
  const [results, setResults]                       = useState([]);
  const [resultsLoading, setResultsLoading]         = useState(false);
  const [resultsError, setResultsError]             = useState('');

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const currentUserId = user?.user_id || user?.id || null;

  // ---------------------------------------------------------------------------
  // Load plan components
  // ---------------------------------------------------------------------------

  const loadComponents = useCallback(async () => {
    if (!user) return;
    setComponentsLoading(true);
    setComponentsError('');
    try {
      // C7 — S689 plan_id scoping: scope component list to this plan when planId is injected.
      // Without this, managers show components from all plans on the tenant (AcceleratorCap pattern).
      const periodId = period?.fiscal_period_id;
      const params = new URLSearchParams();
      if (planId != null) params.set('plan_id', String(planId));
      if (periodId != null) params.set('fiscal_period_id', String(periodId));
      const url = `/api/v1/plan-components${params.toString() ? '?' + params.toString() : ''}`;
      const data = await apiGet(url);
      setComponents(Array.isArray(data) ? data : data?.data || data?.plan_components || []);
    } catch (error) {
      if (getApiErrorCode(error) === 'FEATURE_UNAVAILABLE' || error?.status === 403) {
        setFeatureUnavailable(true);
        return;
      }
      setComponentsError(getErrorMessage(error, 'Unable to load plan components.'));
    } finally {
      setComponentsLoading(false);
    }
  }, [user, period]);

  useEffect(() => { loadComponents(); }, [loadComponents]);

  // Embedded context injection (spec §4.8) — when embedded=true, planComponentId is injected
  useEffect(() => {
    if (embedded && planComponentId != null) {
      setSelectedComponentId(String(planComponentId));
    }
  }, [embedded, planComponentId]);

  // ---------------------------------------------------------------------------
  // Load policies for selected component
  // ---------------------------------------------------------------------------

  const loadPolicies = useCallback(async (componentId) => {
    if (!componentId) return;
    setPoliciesLoading(true);
    setPoliciesError('');
    setPolicies([]);
    try {
      const data = await apiGet(`/api/v1/clawback-policies?plan_component_id=${componentId}`);
      setPolicies(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setPoliciesError(getErrorMessage(error, 'Unable to load clawback policies.'));
    } finally {
      setPoliciesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedComponentId) loadPolicies(selectedComponentId);
  }, [selectedComponentId, loadPolicies]);

  // ---------------------------------------------------------------------------
  // Load policy detail
  // ---------------------------------------------------------------------------

  const loadPolicyDetail = useCallback(async (policyId) => {
    setDetailLoading(true);
    setActionError('');
    try {
      const data = await apiGet(`/api/v1/clawback-policies/${policyId}`);
      setSelectedPolicy(data);
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to load policy detail.'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Load churn events
  // ---------------------------------------------------------------------------

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    setEventsError('');
    try {
      const params = new URLSearchParams();
      if (eventFilters.from)     params.set('churn_date_from', eventFilters.from);
      if (eventFilters.to)       params.set('churn_date_to', eventFilters.to);
      if (eventFilters.customer) params.set('customer_identifier', eventFilters.customer);
      const url = `/api/v1/churn-events${params.toString() ? `?${params}` : ''}`;
      const data = await apiGet(url);
      setEvents(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setEventsError(getErrorMessage(error, 'Unable to load churn events.'));
    } finally {
      setEventsLoading(false);
    }
  }, [eventFilters]);

  useEffect(() => {
    if (activeTab === 'events') loadEvents();
  }, [activeTab, loadEvents]);

  // ---------------------------------------------------------------------------
  // Policy form helpers
  // ---------------------------------------------------------------------------

  const updatePolicyForm = useCallback((field, value) =>
    setPolicyForm((prev) => ({ ...prev, [field]: value })), []);

  const resetPolicyForm = useCallback(() => {
    setPolicyForm(EMPTY_POLICY_FORM);
    setPolicyFormError('');
    setEditingPolicyId(null);
  }, []);

  const validatePolicyForm = useCallback(() => {
    if (!policyForm.policy_name.trim())   return 'Policy name is required.';
    const days = Number(policyForm.protection_window_days);
    if (!Number.isInteger(days) || days <= 0) return 'Protection window must be a positive integer (days).';
    const pct = Number(policyForm.clawback_pct);
    if (isNaN(pct) || pct <= 0 || pct > 1) return 'Clawback % must be between 0.0001 and 1.0000.';
    if (!policyForm.effective_from)       return 'Effective from date is required.';
    if (policyForm.effective_to && policyForm.effective_to < policyForm.effective_from)
      return 'Effective to must be after effective from.';
    return '';
  }, [policyForm]);

  // Toggle a revenue type in the multi-select array
  const toggleRevType = useCallback((code) => {
    setPolicyForm((prev) => {
      const current = prev.applies_to_revenue_types;
      const next = current.includes(code)
        ? current.filter((c) => c !== code)
        : [...current, code];
      return { ...prev, applies_to_revenue_types: next };
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Create policy
  // ---------------------------------------------------------------------------

  const createPolicy = useCallback(async () => {
    if (!selectedComponentId) { setPolicyFormError('Select a plan component first.'); return; }
    const err = validatePolicyForm();
    if (err) { setPolicyFormError(err); return; }
    setPolicySaving(true);
    setPolicyFormError('');
    try {
      const revTypes = policyForm.applies_to_revenue_types;
      const payload = {
        plan_component_id:        Number(selectedComponentId),
        policy_name:              policyForm.policy_name.trim(),
        protection_window_days:   Number(policyForm.protection_window_days),
        clawback_pct:             policyForm.clawback_pct,
        applies_to_revenue_types: revTypes.length > 0 ? revTypes.join(',') : null,
        is_active:                policyForm.is_active,
        effective_from:           policyForm.effective_from,
        effective_to:             policyForm.effective_to || null,
      };
      const created = await apiPost('/api/v1/clawback-policies', payload);
      await loadPolicies(selectedComponentId);
      setShowPolicyForm(false);
      resetPolicyForm();
      showToast('Clawback policy created.');
      const newId = created?.clawback_policy_id;
      if (newId) await loadPolicyDetail(newId);
    } catch (error) {
      setPolicyFormError(getErrorMessage(error, 'Unable to create clawback policy.'));
    } finally {
      setPolicySaving(false);
    }
  }, [loadPolicies, loadPolicyDetail, policyForm, resetPolicyForm, selectedComponentId, showToast, validatePolicyForm]);

  // ---------------------------------------------------------------------------
  // Update policy (PUT — raw fetch)
  // ---------------------------------------------------------------------------

  const updatePolicy = useCallback(async () => {
    if (!editingPolicyId) return;
    const err = validatePolicyForm();
    if (err) { setPolicyFormError(err); return; }
    setPolicySaving(true);
    setPolicyFormError('');
    try {
      const revTypes = policyForm.applies_to_revenue_types;
      const res = await fetch(`/api/v1/clawback-policies/${editingPolicyId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_name:              policyForm.policy_name.trim(),
          protection_window_days:   Number(policyForm.protection_window_days),
          clawback_pct:             policyForm.clawback_pct,
          applies_to_revenue_types: revTypes.length > 0 ? revTypes.join(',') : null,
          is_active:                policyForm.is_active,
          effective_from:           policyForm.effective_from,
          effective_to:             policyForm.effective_to || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.error?.code || body?.code;
        if (code === 'CONFIG_ALREADY_APPROVED')
          throw new Error('This policy is already approved and cannot be edited.');
        if (code === 'CLAWBACK_PCT_INVALID')
          throw new Error('Clawback percentage must be between 0.0001 and 1.0000.');
        throw new Error(body?.error?.message || body?.detail || `Update failed (${res.status})`);
      }
      await loadPolicyDetail(editingPolicyId);
      await loadPolicies(selectedComponentId);
      setShowPolicyForm(false);
      resetPolicyForm();
      showToast('Policy updated.');
    } catch (error) {
      setPolicyFormError(error.message || 'Unable to update policy.');
    } finally {
      setPolicySaving(false);
    }
  }, [editingPolicyId, loadPolicies, loadPolicyDetail, policyForm, resetPolicyForm, selectedComponentId, showToast, validatePolicyForm]);

  const openEditPolicy = useCallback((policy) => {
    setEditingPolicyId(policy.clawback_policy_id);
    setPolicyForm({
      policy_name:               policy.policy_name || '',
      protection_window_days:    String(policy.protection_window_days ?? '90'),
      clawback_pct:              policy.clawback_pct != null ? String(policy.clawback_pct) : '1.0000',
      applies_to_revenue_types:  parseRevTypes(policy.applies_to_revenue_types),
      is_active:                 policy.is_active !== false,
      effective_from:            fmtDate(policy.effective_from) === '—' ? '' : fmtDate(policy.effective_from),
      effective_to:              policy.effective_to ? fmtDate(policy.effective_to) : '',
    });
    setPolicyFormError('');
    setShowPolicyForm(true);
  }, []);

  // ---------------------------------------------------------------------------
  // Approval actions
  // ---------------------------------------------------------------------------

  const submitPolicy = useCallback(async () => {
    if (!selectedPolicy) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/clawback-policies/${selectedPolicy.clawback_policy_id}/submit`, {});
      await loadPolicyDetail(selectedPolicy.clawback_policy_id);
      await loadPolicies(selectedComponentId);
      showToast('Policy submitted for approval.');
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to submit policy.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadPolicies, loadPolicyDetail, selectedComponentId, selectedPolicy, showToast]);

  const approvePolicy = useCallback(async () => {
    if (!selectedPolicy) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/clawback-policies/${selectedPolicy.clawback_policy_id}/approve`, {});
      await loadPolicyDetail(selectedPolicy.clawback_policy_id);
      await loadPolicies(selectedComponentId);
      showToast('Policy approved.');
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_BLOCKED')
        setActionError('You submitted this policy — a different user must approve it.');
      else
        setActionError(getErrorMessage(error, 'Unable to approve policy.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadPolicies, loadPolicyDetail, selectedComponentId, selectedPolicy, showToast]);

  // ---------------------------------------------------------------------------
  // Churn event — single ingest
  // ---------------------------------------------------------------------------

  const updateEventForm = useCallback((field, value) =>
    setEventForm((prev) => ({ ...prev, [field]: value })), []);

  const validateEventForm = useCallback(() => {
    if (!eventForm.customer_identifier.trim()) return 'Customer identifier is required.';
    if (!eventForm.churned_mrr || isNaN(Number(eventForm.churned_mrr)) || Number(eventForm.churned_mrr) <= 0)
      return 'Churned MRR must be a positive number.';
    if (!eventForm.churn_currency_code)        return 'Currency code is required.';
    if (!eventForm.churn_date)                 return 'Churn date is required.';
    if (!eventForm.source_system.trim())       return 'Source system is required.';
    if (!eventForm.source_event_id.trim())     return 'Source event ID is required.';
    return '';
  }, [eventForm]);

  const ingestEvent = useCallback(async () => {
    const err = validateEventForm();
    if (err) { setEventFormError(err); return; }
    setEventSaving(true);
    setEventFormError('');
    try {
      const payload = {
        customer_identifier: eventForm.customer_identifier.trim(),
        churned_mrr:         eventForm.churned_mrr,
        churn_currency_code: eventForm.churn_currency_code,
        churn_date:          eventForm.churn_date,
        churn_reason_code:   eventForm.churn_reason_code.trim() || null,
        source_system:       eventForm.source_system.trim(),
        source_event_id:     eventForm.source_event_id.trim(),
      };
      await apiPost('/api/v1/churn-events', payload);
      await loadEvents();
      setShowEventForm(false);
      setEventForm(EMPTY_EVENT_FORM);
      showToast('Churn event ingested.');
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'CHURN_EVENT_DUPLICATE')
        setEventFormError('This source_system + source_event_id combination already exists. Duplicate events are blocked.');
      else
        setEventFormError(getErrorMessage(error, 'Unable to ingest churn event.'));
    } finally {
      setEventSaving(false);
    }
  }, [eventForm, loadEvents, showToast, validateEventForm]);

  // ---------------------------------------------------------------------------
  // CSV import
  // ---------------------------------------------------------------------------

  const handleCsvImport = useCallback(async () => {
    if (!csvFile) { setCsvImportError('Select a CSV file first.'); return; }
    setCsvImporting(true);
    setCsvImportError('');
    setCsvImportResult(null);
    try {
      // multipart/form-data — cannot use apiPost
      const formData = new FormData();
      formData.append('file', csvFile);
      const res = await fetch('/api/v1/churn-events/import', {
        method: 'POST',
        credentials: 'include',
        body: formData,
        // Content-Type intentionally omitted — browser sets multipart boundary automatically
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || body?.detail || `Import failed (${res.status})`);
      }
      const result = await res.json();
      setCsvImportResult(result);
      setCsvFile(null);
      await loadEvents();
      showToast(`Import complete — ${result.rows_loaded ?? 0} rows loaded.`);
    } catch (error) {
      setCsvImportError(error.message || 'Unable to import churn events.');
    } finally {
      setCsvImporting(false);
    }
  }, [csvFile, loadEvents, showToast]);

  // ---------------------------------------------------------------------------
  // Results tab
  // ---------------------------------------------------------------------------

  const loadResults = useCallback(async () => {
    if (!runIdInput.trim()) { setResultsError('Calculation run ID is required.'); return; }
    setResultsLoading(true);
    setResultsError('');
    setResults([]);
    try {
      let url = `/api/v1/churn-clawback-results?calculation_run_id=${encodeURIComponent(runIdInput.trim())}`;
      if (positionIdInput.trim()) url += `&position_id=${encodeURIComponent(positionIdInput.trim())}`;
      const data = await apiGet(url);
      setResults(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setResultsError(getErrorMessage(error, 'Unable to load clawback results.'));
    } finally {
      setResultsLoading(false);
    }
  }, [positionIdInput, runIdInput]);

  // ---------------------------------------------------------------------------
  // Feature gate / auth guard
  // ---------------------------------------------------------------------------

  if (featureUnavailable) return <GateStatusBanner feature="Churn Clawback" />;

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Churn Clawback</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Policy detail view
  // ---------------------------------------------------------------------------

  if (selectedPolicy) {
    const state   = deriveState(selectedPolicy);
    const isDraft = state === 'draft';
    const revTypes = parseRevTypes(selectedPolicy.applies_to_revenue_types);

    return (
      <section className="flex flex-col gap-6">
        {toast && (
          <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
            {toast}
          </div>
        )}

        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button type="button" className={btnSecondary}
              onClick={() => { setSelectedPolicy(null); setActionError(''); setShowPolicyForm(false); resetPolicyForm(); }}>
              ← Back to policies
            </button>
            <h2 className="mt-3 text-tok-xl font-semibold text-[rgb(var(--color-text))]">
              {selectedPolicy.policy_name}
            </h2>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              Clawback policy · Effective {fmtDate(selectedPolicy.effective_from)}
              {selectedPolicy.effective_to ? ` → ${fmtDate(selectedPolicy.effective_to)}` : ' (open-ended)'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={state} />
            {isDraft && (
              <button type="button" className={btnSecondary} onClick={() => openEditPolicy(selectedPolicy)}>
                Edit
              </button>
            )}
          </div>
        </header>

        {actionError && (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {actionError}
          </div>
        )}

        {/* Policy detail card */}
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-3 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Policy details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-tok-sm">
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Protection window</dt>
              <dd className="text-[rgb(var(--color-text))] font-medium">
                {selectedPolicy.protection_window_days} day{selectedPolicy.protection_window_days !== 1 ? 's' : ''}
              </dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Clawback %</dt>
              <dd className="text-[rgb(var(--color-text))] font-medium">{fmtPct(selectedPolicy.clawback_pct)}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Applies to revenue types</dt>
              <dd className="text-[rgb(var(--color-text))]">
                {revTypes.length > 0
                  ? revTypes.map((c) => REVENUE_TYPE_OPTIONS.find((o) => o.value === c)?.label || c).join(', ')
                  : 'All revenue types'}
              </dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Active</dt>
              <dd className="text-[rgb(var(--color-text))]">{selectedPolicy.is_active ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Effective from</dt>
              <dd className="text-[rgb(var(--color-text))]">{fmtDate(selectedPolicy.effective_from)}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Effective to</dt>
              <dd className="text-[rgb(var(--color-text))]">
                {selectedPolicy.effective_to ? fmtDate(selectedPolicy.effective_to) : 'Open-ended'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Inline edit form */}
        {showPolicyForm && editingPolicyId === selectedPolicy.clawback_policy_id && (
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Edit policy</h3>
            {policyFormError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                {policyFormError}
              </div>
            )}
            {renderPolicyFormFields({ policyForm, updatePolicyForm, toggleRevType, inputClass, labelClass })}
            <div className="flex items-center gap-2">
              <button type="button" className={btnPrimary} onClick={updatePolicy} disabled={policySaving}>
                {policySaving ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className={btnSecondary}
                onClick={() => { setShowPolicyForm(false); resetPolicyForm(); }} disabled={policySaving}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Approval panel */}
        {detailLoading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>
        ) : (
          <ApprovalPanel
            variant="plan"
            currentState={state}
            submittedBy={selectedPolicy.submitted_by ?? null}
            currentUserId={currentUserId}
            onSubmit={state === 'draft' ? submitPolicy : null}
            onApprove={state === 'pending_approval' ? approvePolicy : null}
            onReject={null}
            approvedBy={selectedPolicy.approved_by ?? null}
            approvedAt={selectedPolicy.approved_at ?? null}
            loading={actionLoading}
          />
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Main tabbed view
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-6">
      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <header>
        {mode === 'wizard' && (
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">
            Stage 8 · P3-04
          </p>
        )}
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Churn Clawback</h2>
        <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Configure clawback policies that recover commission when customers churn within a protection window.
          Ingest churn events from your CRM or billing system. Review clawback results after each run.
        </p>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((tab) => (
          <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)}
            className={[
              'px-4 py-2 text-tok-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]',
            ].join(' ')}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Policies tab ── */}
      {activeTab === 'policies' && (
        <div className="flex flex-col gap-4">
          {/* Component selector — hidden when embedded (spec §4.8) */}
          <div className="flex items-end gap-3 flex-wrap">
            {!embedded && <label className={labelClass} style={{ flex: '1', minWidth: '240px' }}>
              Plan component
              {componentsLoading && <span className="ml-2 text-tok-xs text-[rgb(var(--color-text-muted))]">Loading…</span>}
              <select className={inputClass} value={selectedComponentId}
                onChange={(e) => {
                  setSelectedComponentId(e.target.value);
                  setPolicies([]);
                  setShowPolicyForm(false);
                  resetPolicyForm();
                }}>
                <option value="">Select a plan component…</option>
                {components.map((c) => (
                  <option key={c.plan_component_id} value={c.plan_component_id}>
                    {c.role_code || c.role_id || `Component ${c.plan_component_id}`}
                    {c.segment_code ? ` — ${c.segment_code}` : ''}
                  </option>
                ))}
              </select>
            </label>}
            {selectedComponentId && !showPolicyForm && (
              <button type="button" className={btnPrimary}
                onClick={() => { resetPolicyForm(); setShowPolicyForm(true); }}>
                + New policy
              </button>
            )}
          </div>

          {componentsError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {componentsError}
            </div>
          )}

          {/* Policy create form */}
          {showPolicyForm && !editingPolicyId && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">New clawback policy</h3>
              {policyFormError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {policyFormError}
                </div>
              )}
              {renderPolicyFormFields({ policyForm, updatePolicyForm, toggleRevType, inputClass, labelClass })}
              <div className="flex items-center gap-2">
                <button type="button" className={btnPrimary} onClick={createPolicy} disabled={policySaving}>
                  {policySaving ? 'Creating…' : 'Create policy'}
                </button>
                <button type="button" className={btnSecondary}
                  onClick={() => { setShowPolicyForm(false); resetPolicyForm(); }} disabled={policySaving}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Policies list */}
          {selectedComponentId && (
            <>
              {policiesLoading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading policies…</p>}
              {policiesError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {policiesError}
                </div>
              )}
              {!policiesLoading && !policiesError && policies.length === 0 && (
                <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                    No clawback policies for this component. Create one to protect against churn within
                    the defined protection window.
                  </p>
                </div>
              )}
              {policies.length > 0 && (
                <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
                  <table className="min-w-full text-tok-sm border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Policy name</th>
                        <th className={thClass}>Window (days)</th>
                        <th className={thClass}>Clawback %</th>
                        <th className={thClass}>Revenue types</th>
                        <th className={thClass}>Effective from</th>
                        <th className={thClass}>Status</th>
                        <th className={thClass} aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {policies.map((p) => (
                        <tr key={p.clawback_policy_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass + ' font-medium'}>{p.policy_name}</td>
                          <td className={tdClass}>{p.protection_window_days}</td>
                          <td className={tdClass}>{fmtPct(p.clawback_pct)}</td>
                          <td className={tdClass}>
                            {p.applies_to_revenue_types
                              ? parseRevTypes(p.applies_to_revenue_types)
                                  .map((c) => REVENUE_TYPE_OPTIONS.find((o) => o.value === c)?.label || c)
                                  .join(', ')
                              : <span className="italic text-[rgb(var(--color-text-muted))]">All types</span>}
                          </td>
                          <td className={tdClass}>{fmtDate(p.effective_from)}</td>
                          <td className={tdClass}><StatusPill status={deriveState(p)} /></td>
                          <td className={tdClass}>
                            <button type="button"
                              className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
                              onClick={() => loadPolicyDetail(p.clawback_policy_id)}>
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {!selectedComponentId && !componentsLoading && components.length === 0 && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                No plan components found{period?.period_code ? ` for ${period.period_code}` : ''}.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Churn Events tab ── */}
      {activeTab === 'events' && (
        <div className="flex flex-col gap-4">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Ingest churn events individually or via CSV batch import.
            Each event is deduplicated on source_system + source_event_id.
            The engine matches events to credits using customer_identifier.
          </p>

          {/* Filters */}
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-4 flex items-end gap-3 flex-wrap shadow-sm">
            <label className={labelClass} style={{ minWidth: '140px' }}>
              From date
              <input type="date" className={inputClass}
                value={eventFilters.from}
                onChange={(e) => setEventFilters((prev) => ({ ...prev, from: e.target.value }))} />
            </label>
            <label className={labelClass} style={{ minWidth: '140px' }}>
              To date
              <input type="date" className={inputClass}
                value={eventFilters.to}
                onChange={(e) => setEventFilters((prev) => ({ ...prev, to: e.target.value }))} />
            </label>
            <label className={labelClass} style={{ flex: '1', minWidth: '180px' }}>
              Customer ID
              <input type="text" className={inputClass}
                value={eventFilters.customer}
                onChange={(e) => setEventFilters((prev) => ({ ...prev, customer: e.target.value }))}
                placeholder="Partial match" />
            </label>
            <button type="button" className={btnSecondary} onClick={loadEvents} disabled={eventsLoading}>
              {eventsLoading ? 'Loading…' : 'Search'}
            </button>
          </div>

          {/* Single event ingest form */}
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Ingest events</h3>
            {!showEventForm && (
              <button type="button" className={btnSecondary}
                onClick={() => { setEventForm(EMPTY_EVENT_FORM); setEventFormError(''); setShowEventForm(true); }}>
                + Single event
              </button>
            )}
          </div>

          {showEventForm && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
              <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">New churn event</h4>
              {eventFormError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {eventFormError}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className={labelClass}>
                  Customer identifier <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="text" className={inputClass} value={eventForm.customer_identifier}
                    onChange={(e) => updateEventForm('customer_identifier', e.target.value)}
                    placeholder="e.g. CUST-00123 (must match credits)" />
                </label>
                <label className={labelClass}>
                  Churn date <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="date" className={inputClass} value={eventForm.churn_date}
                    onChange={(e) => updateEventForm('churn_date', e.target.value)} />
                </label>
                <label className={labelClass}>
                  Churned MRR <span className="text-[rgb(var(--color-danger))]">*</span>
                  <div className="flex gap-2">
                    <input type="number" min="0.01" step="0.01" className={inputClass}
                      value={eventForm.churned_mrr}
                      onChange={(e) => updateEventForm('churned_mrr', e.target.value)}
                      placeholder="e.g. 4500.00" />
                    <select className={inputClass + ' !mt-0'} style={{ maxWidth: '100px' }}
                      value={eventForm.churn_currency_code}
                      onChange={(e) => updateEventForm('churn_currency_code', e.target.value)}>
                      {CURRENCY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </label>
                <label className={labelClass}>
                  Churn reason (optional)
                  <input type="text" className={inputClass} value={eventForm.churn_reason_code}
                    onChange={(e) => updateEventForm('churn_reason_code', e.target.value)}
                    placeholder="e.g. price, competitor, product" />
                </label>
                <label className={labelClass}>
                  Source system <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="text" className={inputClass} value={eventForm.source_system}
                    onChange={(e) => updateEventForm('source_system', e.target.value)}
                    placeholder="e.g. ChurnZero, Salesforce" />
                </label>
                <label className={labelClass}>
                  Source event ID <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="text" className={inputClass} value={eventForm.source_event_id}
                    onChange={(e) => updateEventForm('source_event_id', e.target.value)}
                    placeholder="External event identifier for dedup" />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" className={btnPrimary} onClick={ingestEvent} disabled={eventSaving}>
                  {eventSaving ? 'Ingesting…' : 'Ingest event'}
                </button>
                <button type="button" className={btnSecondary}
                  onClick={() => { setShowEventForm(false); setEventForm(EMPTY_EVENT_FORM); setEventFormError(''); }}
                  disabled={eventSaving}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* CSV batch import */}
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-3 shadow-sm">
            <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">CSV batch import</h4>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              Required columns: customer_identifier, churned_mrr, churn_currency_code, churn_date,
              source_system, source_event_id. Optional: churn_reason_code.
              Duplicate rows (same source_system + source_event_id) are skipped, not errored.
            </p>
            {csvImportError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                {csvImportError}
              </div>
            )}
            {csvImportResult && (
              <div className="rounded-lg bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 px-4 py-3 text-tok-sm">
                <p className="font-medium text-[rgb(var(--color-success))]">Import complete</p>
                <p className="mt-1 text-[rgb(var(--color-text-muted))]">
                  {csvImportResult.rows_received ?? '?'} received ·
                  {' '}{csvImportResult.rows_loaded ?? '?'} loaded ·
                  {' '}{csvImportResult.rows_rejected ?? 0} rejected ·
                  {' '}{csvImportResult.duplicate_count ?? 0} duplicates skipped
                </p>
              </div>
            )}
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text))]">
                <input type="file" accept=".csv"
                  className="block text-tok-sm text-[rgb(var(--color-text-muted))] file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border file:border-[rgb(var(--color-border))] file:text-tok-sm file:font-medium file:bg-[rgb(var(--color-surface))] file:text-[rgb(var(--color-text))] hover:file:bg-[rgb(var(--color-surface-muted))]"
                  onChange={(e) => { setCsvFile(e.target.files?.[0] || null); setCsvImportResult(null); setCsvImportError(''); }} />
              </label>
              <button type="button" className={btnPrimary}
                onClick={handleCsvImport}
                disabled={csvImporting || !csvFile}>
                {csvImporting ? 'Importing…' : 'Import CSV'}
              </button>
            </div>
          </div>

          {eventsError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {eventsError}
            </div>
          )}

          {/* Events table */}
          {!eventsLoading && events.length === 0 && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-6 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                No churn events found. Ingest events or adjust your filters.
              </p>
            </div>
          )}

          {events.length > 0 && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Customer ID</th>
                    <th className={thClass}>Churn date</th>
                    <th className={thClass}>Churned MRR</th>
                    <th className={thClass}>Currency</th>
                    <th className={thClass}>Reason</th>
                    <th className={thClass}>Source system</th>
                    <th className={thClass}>Source event ID</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.churn_event_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className={tdClass + ' font-medium'}>{e.customer_identifier}</td>
                      <td className={tdClass}>{fmtDate(e.churn_date)}</td>
                      <td className={tdClass}>{fmtMoney(e.churned_mrr)}</td>
                      <td className={tdClass}>{e.churn_currency_code}</td>
                      <td className={tdClass}>{e.churn_reason_code || '—'}</td>
                      <td className={tdClass}>{e.source_system}</td>
                      <td className={tdClass + ' text-tok-xs text-[rgb(var(--color-text-muted))]'}>
                        {e.source_event_id}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Results tab ── */}
      {activeTab === 'results' && (
        <div className="flex flex-col gap-4">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Review clawback amounts applied during a calculation run. Shows original commission,
            clawback amount, days since close, and whether the event fell within the protection window.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className={labelClass}>
              Calculation run ID <span className="text-[rgb(var(--color-danger))]">*</span>
              <input type="text" className={inputClass} value={runIdInput}
                onChange={(e) => setRunIdInput(e.target.value)}
                placeholder="UUID from calculation run" />
            </label>
            <label className={labelClass}>
              Position ID (optional)
              <input type="text" className={inputClass} value={positionIdInput}
                onChange={(e) => setPositionIdInput(e.target.value)}
                placeholder="Filter to a single position" />
            </label>
          </div>
          <div>
            <button type="button" className={btnPrimary} onClick={loadResults} disabled={resultsLoading}>
              {resultsLoading ? 'Loading…' : 'Load results'}
            </button>
          </div>
          {resultsError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {resultsError}
            </div>
          )}
          {!resultsLoading && results.length === 0 && runIdInput && !resultsError && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">
              No clawback results for this run. Either no churn events matched active policies,
              or the run ID is incorrect.
            </p>
          )}
          {results.length > 0 && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Position</th>
                    <th className={thClass}>Customer</th>
                    <th className={thClass}>Churn event</th>
                    <th className={thClass}>Days since close</th>
                    <th className={thClass}>In window</th>
                    <th className={thClass}>Original commission</th>
                    <th className={thClass}>Clawback amount</th>
                    <th className={thClass}>Currency</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.clawback_result_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className={tdClass + ' text-tok-xs text-[rgb(var(--color-text-muted))]'}>{r.position_id}</td>
                      <td className={tdClass}>{r.customer_identifier || r.churn_event_id}</td>
                      <td className={tdClass + ' text-tok-xs text-[rgb(var(--color-text-muted))]'}>{r.churn_event_id}</td>
                      <td className={tdClass}>{r.days_since_close ?? '—'}</td>
                      <td className={tdClass}>
                        <span className={`inline-block px-2 py-0.5 rounded text-tok-xs font-medium ${
                          r.within_window
                            ? 'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]'
                            : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'
                        }`}>
                          {r.within_window ? 'Yes — clawback applied' : 'No — outside window'}
                        </span>
                      </td>
                      <td className={tdClass}>{fmtMoney(r.original_commission)}</td>
                      <td className={tdClass + ' font-medium text-[rgb(var(--color-danger))]'}>
                        {r.within_window ? `−${fmtMoney(r.clawback_amount)}` : '—'}
                      </td>
                      <td className={tdClass}>{r.clawback_currency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Extracted policy form fields — used in both create and edit contexts
// ---------------------------------------------------------------------------

function renderPolicyFormFields({ policyForm, updatePolicyForm, toggleRevType, inputClass, labelClass }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className={labelClass + ' sm:col-span-2'}>
          Policy name <span className="text-[rgb(var(--color-danger))]">*</span>
          <input type="text" className={inputClass} value={policyForm.policy_name}
            onChange={(e) => updatePolicyForm('policy_name', e.target.value)}
            placeholder="e.g. Standard SaaS Churn Protection" />
        </label>
        <label className={labelClass}>
          Protection window (days) <span className="text-[rgb(var(--color-danger))]">*</span>
          <input type="number" min="1" step="1" className={inputClass}
            value={policyForm.protection_window_days}
            onChange={(e) => updatePolicyForm('protection_window_days', e.target.value)}
            placeholder="e.g. 90, 180, 365" />
          <span className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Days from deal close within which churn triggers clawback
          </span>
        </label>
        <label className={labelClass}>
          Clawback % <span className="text-[rgb(var(--color-danger))]">*</span>
          <input type="number" min="0.0001" max="1" step="0.0001" className={inputClass}
            value={policyForm.clawback_pct}
            onChange={(e) => updatePolicyForm('clawback_pct', e.target.value)}
            placeholder="e.g. 1.0000 for 100%, 0.5000 for 50%" />
          <span className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Fraction of original commission to recover. 1.0 = full recovery.
          </span>
        </label>
        <label className={labelClass}>
          Effective from <span className="text-[rgb(var(--color-danger))]">*</span>
          <input type="date" className={inputClass} value={policyForm.effective_from}
            onChange={(e) => updatePolicyForm('effective_from', e.target.value)} />
        </label>
        <label className={labelClass}>
          Effective to
          <input type="date" className={inputClass} value={policyForm.effective_to}
            onChange={(e) => updatePolicyForm('effective_to', e.target.value)} />
        </label>
      </div>

      {/* Revenue type filter — multi-select checkboxes */}
      <div>
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text))] mb-2">
          Applies to revenue types
          <span className="ml-2 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">
            Leave all unchecked to apply to all revenue types
          </span>
        </p>
        <div className="flex flex-wrap gap-3">
          {REVENUE_TYPE_OPTIONS.map((opt) => (
            <label key={opt.value}
              className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text))] cursor-pointer select-none">
              <input type="checkbox"
                className="rounded border-[rgb(var(--color-border))]"
                checked={policyForm.applies_to_revenue_types.includes(opt.value)}
                onChange={() => toggleRevType(opt.value)} />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text))] cursor-pointer select-none">
        <input type="checkbox"
          className="rounded border-[rgb(var(--color-border))]"
          checked={policyForm.is_active}
          onChange={(e) => updatePolicyForm('is_active', e.target.checked)} />
        Policy is active
      </label>
    </>
  );
}
